<template>
	<div class="game-list">
		<transition name="fade" tag="div" mode="out-in">
			<div :key="filter" class="game-list-container">
				<div v-for="game in gameList" :key="game.name" ref="game-cards" class="game-card-container">
					<div class="game-card">
						<div class="contents" :class="{ mobile: isMobile }" @click="gameCardClick(game)" @mouseenter="trackHover(game)">
							<cdnImage :src="game.gameCardImage" absolute />
							<div v-if="game.gameCardBadge" class="game-card-badge">
								<span>{{ getLocaleString(game.gameCardBadge) }}</span>
							</div>
							<div class="blur"></div>
							<div class="hover-info">
								<FormButton
									:label="$t('main.learn-more')"
									:button-click="() => navToDetails(game)"
									class="primary-button"
								></FormButton>
								<a
									v-if="game.enableCustomDownloadButton"
									class="secondary-button custom"
									:href="appendArenaSidQS(getLocaleString(game.customDownloadButtonLink))"
									target="_blank"
									rel="external"
									@click="trackDownload(game, 'Custom Button')"
								>
									<span>{{ getLocaleString(game.customDownloadButtonText) }}</span>
								</a>
								<button
									v-if="!game.enableCustomDownloadButton && (isWindowSupport(game) || isMacSupport(game))"
									class="secondary-button"
									@click="download(game)"
								>
									<img
										v-if="isWindows || isMac"
										class="download-icon"
										:src="
											isWindows
												? require('../../../images/icons/windows.svg')
												: require('../../../images/icons/apple.svg')
										"
									/>
									<span>{{ $t('main.download') }}</span>
								</button>
								<a
									v-if="!game.enableCustomDownloadButton && isWindows && game.steam && !game.pc"
									class="secondary-button steam"
									:href="game.steam"
									target="_blank"
									rel="external"
									@click="trackDownload(game, 'Steam')"
								>
									<svgIcon icon-holder-class="download-icon" icon-id="icon-steam" />
									<span>{{ $t('main.play-on-steam') }}</span>
								</a>
								<span
									v-if="
										((!isWindows || game.enableCustomDownloadButton) && (game.pc || game.steam)) ||
											game.xbox ||
											game.psn ||
											game.ios ||
											game.android ||
											((!isMac || game.enableCustomDownloadButton) && game.pcMac)
									"
									class="also-available"
									>{{
										$t(
											`main.${
												!isGameSupported(game) || game.enableCustomDownloadButton
													? 'download-for'
													: 'also-available-on'
											}`
										)
									}}</span
								>
								<div class="other-platforms">
									<span
										v-if="(!isWindows || game.enableCustomDownloadButton) && (game.pc || game.steam)"
										:class="{ active: (isWindows && game.pc) || game.steam }"
										@click="pcClick(game)"
										>{{ $t('main.filters.pc') }}</span
									>
									<span
										v-if="(!isMac || game.enableCustomDownloadButton) && game.pcMac"
										:class="{ active: isMac && game.pcMac }"
										>{{ $t('main.filters.mac') }}</span
									>
									<a
										v-if="game.xbox"
										:href="game.xbox"
										target="_blank"
										rel="external"
										@click="trackDownload(game, 'xbox')"
										>{{ $t('main.filters.xbox') }}</a
									>
									<a
										v-if="game.psn"
										:href="game.psn"
										target="_blank"
										rel="external"
										@click="trackDownload(game, 'psn')"
										>{{ $t('main.filters.psn') }}</a
									>
									<a
										v-if="game.ios"
										:href="game.ios"
										target="_blank"
										rel="external"
										@click="trackDownload(game, 'App Store')"
										>{{ $t('main.filters.ios') }}</a
									>
									<a
										v-if="game.android"
										:href="game.android"
										target="_blank"
										rel="external"
										@click="trackDownload(game, 'Google Play Store')"
										>{{ $t('main.filters.android') }}</a
									>
								</div>
							</div>
						</div>
						<div class="info">
							<div class="name">{{ game.name }}</div>
							<div class="genre-platform">
								<span>{{ game.genre }}</span>
								<div class="platforms">
									<svgIcon v-if="game.pc || game.steam" icon-id="icon-ext-windows" />
									<svgIcon v-if="game.pcMac" icon-id="icon-apple" />
									<svgIcon v-if="game.xbox" icon-id="icon-ext-xbox" />
									<svgIcon v-if="game.psn" icon-id="icon-psn" />
									<img v-if="game.ios" :src="require('../../../images/icons/ios.svg')" />
									<img v-if="game.android" :src="require('../../../images/icons/android.svg')" />
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</transition>
	</div>
</template>

<script>
import { mapState } from 'vuex';
import { getLocaleString } from '../../../plugins/helpers';
import svgIcon from '@arena/toolkit/src/components/common/icon-svg';
import { FormButton } from '@arena/toolkit';
import downloadGame from '../../../plugins/launcherDownload';
import cdnImage from '@/components/cdn-image';

const IN_VIEW_PERCENTAGE = (2 / 3) * 100;

export default {
	name: 'GameList',
	components: {
		svgIcon,
		FormButton,
		cdnImage,
	},
	props: {
		gameList: {
			type: Array,
			default: () => [],
		},
		filter: {
			type: String,
			default: '',
		},
	},
	data() {
		return {};
	},
	computed: {
		...mapState(['isIOS', 'isAndroid', 'isWindows', 'isMac']),
		isMobile() {
			return this.isIOS || this.isAndroid;
		},
		NXLogSessionId() {
			return this.$cookie.get('NxLogSession') ? this.$cookie.get('NxLogSession') : new Date().toISOString();
		},
		language() {
			return this.$i18n.locale;
		},
		region() {
			return environment.region;
		},
		memberShipNo() {
			return this.region === 'global' ? 1 : this.region === 'th' ? 3 : this.region === 'tw' ? 4 : this.region === 'sea' ? 5 : null;
		},
	},
	watch: {
		gameList() {
			this.$nextTick(() => {
				this.isInView();
			});
		},
	},
	mounted() {
		this.$nextTick(() => {
			window.addEventListener('scroll', this.isInView, true);
			window.addEventListener('resize', this.isInView);

			this.isInView();
		});
	},
	destroyed() {
		window.removeEventListener('scroll', this.isInView);
		window.removeEventListener('resize', this.isInView);
	},
	methods: {
		gameCardClick(game) {
			if (!this.isMobile) return;
			dataLayer.push({
				event: 'Game Card Mobile Tap',
				gameCardSupported: this.isGameSupportedGaName(game),
				cardGameName: game.name,
				cardGameID: game.productID,
			});
			this.navToDetails(game, true);
		},
		navToDetails(game, skipTracking) {
			if (!skipTracking) {
				dataLayer.push({
					event: 'Game Card Engagement',
					gameCardEngagementType: 'Learn More',
					gameCardSupported: this.isGameSupportedGaName(game),
					cardGameName: game.name,
					cardGameID: game.productID,
				});
			}
			this.$router.push({
				name: 'details',
				params: {
					title: game.name,
				},
			});
		},
		openSteamLink(game) {
			this.trackDownload(game, 'Steam');
			this.openLink(game.steam);
		},
		pcClick(game) {
			if (this.isWindows) {
				if (game.pc) {
					this.download(game);
					return;
				} else if (game.steam) {
					this.openSteamLink(game);
				}
			} else {
				if (game.pc) {
					return;
				} else if (game.steam) {
					this.openSteamLink(game);
				}
			}
		},
		openLink(url) {
			window.open(url, '_blank');
		},
		download(game) {
			if (!this.isWindows && !this.isMac) return;
			this.trackDownload(game, 'Download');
			downloadGame(game.productID, this.NXLogSessionId, this.memberShipNo, this.language, this.$route.query.partnerKey, this.isMac);
		},
		trackDownload(game, type) {
			dataLayer.push({
				event: 'Game Card Engagement',
				gameCardEngagementType: type,
				gameCardSupported: this.isGameSupportedGaName(game),
				cardGameName: game.name,
				cardGameID: game.productID,
			});
		},
		trackHover(game) {
			if (this.isMobile) return;
			dataLayer.push({
				event: 'Game Card Hovered',
				gameCardSupported: this.isGameSupportedGaName(game),
				cardGameName: game.name,
				cardGameID: game.productID,
			});
		},
		isInView() {
			if (!this.$refs['game-cards']) return;
			const len = this.$refs['game-cards'].length;
			for (let i = 0; i < len; i++) {
				const game = this.gameList[i];
				if (!game.viewedEventFired) {
					const gameCard = this.$refs['game-cards'][i];
					const rect = gameCard.getBoundingClientRect(),
						windowHeight = window.innerHeight || document.documentElement.clientHeight,
						isInView = !(
							Math.floor(100 - ((rect.top >= 0 ? 0 : rect.top) / +-(rect.height / 1)) * 100) < IN_VIEW_PERCENTAGE ||
							Math.floor(100 - ((rect.bottom - windowHeight) / rect.height) * 100) < IN_VIEW_PERCENTAGE
						);

					if (isInView) {
						game.viewedEventFired = true;
						dataLayer.push({
							event: 'Viewed Game Card',
							gameCardSupported: this.isGameSupportedGaName(game),
							cardGameName: game.name,
							cardGameID: game.productID,
						});
					}
				}
			}
		},
		isGameSupported(game) {
			return (
				(this.isWindows && (game.pc || game.steam)) ||
				(this.isIOS && game.ios) ||
				(this.isAndroid && game.android) ||
				(this.isMac && game.pcMac)
			);
		},
		isGameSupportedGaName(game) {
			return this.isGameSupported(game) ? 'Supported' : 'Unsupported';
		},
		isWindowSupport(game) {
			return this.isWindows && game.pc;
		},
		isMacSupport(game) {
			return this.isMac && game.pcMac;
		},
		getLocaleString(data) {
			return getLocaleString(data, this.language);
		},
	},
};
</script>

<style lang="less" scoped>
.fade-enter-active,
.fade-leave-active {
	transition: all 0.2s;
}
.fade-enter,
.fade-leave-to {
	opacity: 0;
}
.fade-enter-to,
.fade-leave {
	opacity: 1;
}

.game-list {
	margin: auto;
	min-height: 500px;
	user-select: none;
	width: 335px;
	.game-list-container {
		display: flex;
		flex-wrap: wrap;
		margin: 0 -6px;
		.game-card-container {
			margin: 0 6px;
			.game-card {
				height: 318px;
				width: 161px;
				.contents {
					background-position: center;
					background-size: cover;
					border-radius: 5px;
					height: 212px;
					overflow: hidden;
					position: relative;
					width: 100%;
					&.mobile {
						.hover-info,
						.blur {
							display: none;
						}
					}
					.game-card-badge {
						align-items: center;
						background-color: #fa5951;
						border-radius: 0 5px 0 0;
						bottom: 0;
						box-shadow: 0 0 40px 0 rgba(0, 0, 0, 0.5);
						color: white;
						display: flex;
						font-family: Roboto;
						font-size: 11px;
						font-weight: bold;
						height: 23px;
						justify-content: center;
						left: 0;
						padding: 7px;
						position: absolute;
						text-transform: uppercase;
						width: auto;
					}
					&:hover {
						.hover-info,
						.blur {
							opacity: 1;
							visibility: visible;
						}
					}
					.hover-info {
						align-items: center;
						background-color: rgba(41, 42, 46, 0.9);
						border: 1px solid rgba(255, 255, 255, 0.14);
						display: flex;
						flex-direction: column;
						height: 100%;
						justify-content: center;
						opacity: 0;
						position: relative;
						transition: visibility 0s, opacity 0.2s linear;
						user-select: none;
						visibility: hidden;
						width: 100%;
						button,
						a.steam,
						a.custom {
							align-items: center;
							background-color: transparent;
							border: 1px solid rgba(255, 255, 255, 0.5);
							border-radius: 5px;
							cursor: pointer;
							display: flex;
							font-weight: bold;
							height: 36px;
							justify-content: center;
							margin: 0;
							outline: 0;
							padding: 0 5px;
							text-transform: uppercase;
							transition: background-color 0.2s, background-color 0.2s, border-color 0.2s, color 0.2s;
							width: 133px;
							&.primary-button {
								color: rgba(255, 255, 255, 0.7);
								font-size: 10px;
								&:not(:active):hover {
									border: 1px solid rgba(255, 255, 255, 1);
								}
								&:active {
									border: 1px solid rgba(255, 255, 255, 0.4);
								}
							}
							&.secondary-button {
								background-color: #0077ff;
								border: none;
								color: white;
								font-size: 10.5px;
								margin-top: 10px;
								&:not(:active):hover {
									background-color: #4ca1ff;
								}
								&:active {
									background-color: #0052b3;
									color: #b3b3b4;
								}
							}
							&:not(:active):hover {
								color: white;
								.download-icon {
									opacity: 1;
								}
							}
							&:active {
								color: rgba(255, 255, 255, 0.4);
								.download-icon {
									opacity: 0.5;
								}
							}
							.download-icon {
								height: 13px;
								margin-right: 5px;
								width: 13px;
							}
						}
					}
					.blur {
						background: url('~@/images/game-card/blur.png') no-repeat center;
						background-size: contain;
						display: block;
						height: 139px;
						opacity: 0;
						pointer-events: none;
						position: absolute;
						top: 0;
						transition: visibility 0s, opacity 0.2s linear;
						visibility: hidden;
						width: 100%;
					}
					.also-available {
						color: rgba(255, 255, 255, 0.5);
						font-size: 11px;
						margin-top: 15px;
						text-align: center;
					}
					.other-platforms {
						color: rgba(255, 255, 255, 0.8);
						font-size: 12px;
						letter-spacing: -0.24px;
						margin-top: 5px;
						text-align: center;
						a,
						a:visited {
							color: rgba(255, 255, 255, 0.8);
							text-decoration: underline;
							&:hover {
								color: white;
							}
							&:active {
								color: #7e7e7f;
							}
						}
						:not(:first-child):before {
							content: '';
							margin-left: 2px;
						}
						:not(:last-child):after {
							content: ',';
						}
						.active {
							text-decoration: underline;
							&:hover {
								color: white;
								cursor: pointer;
							}
							&:active {
								color: #7e7e7f;
							}
						}
					}
				}
				.info {
					font-size: 14.5px;
					letter-spacing: -0.29px;
					line-height: 1.55;
					padding: 0 4px;
					.name {
						color: white;
						margin: 16px 0 6px 0;
					}
					.genre-platform {
						align-items: center;
						color: rgba(255, 255, 255, 0.5);
						display: flex;
						flex-wrap: wrap;
						justify-content: space-between;
						span {
							margin-bottom: 5px;
						}
						.platforms {
							display: flex;
							margin-bottom: 5px;
							opacity: 0.3;
							img:not(:last-child),
							svg:not(:last-child) {
								margin-right: 7px;
							}
							img,
							svg {
								height: 13px;
								width: 13px;
							}
						}
					}
				}
			}
		}
	}
}

@media (min-width: 548px) {
	.game-list {
		width: 507px;
	}
}

@media (min-width: 768px) {
	.game-list {
		width: 724px;
		.game-list-container {
			.game-card-container {
				.game-card {
					height: 332px;
					width: 172px;
					.contents {
						height: 226px;
						.hover-info {
							button,
							a.steam,
							a.custom {
								width: 144px;
							}
						}
					}
				}
			}
		}
	}
}

@media (min-width: 1024px) {
	.game-list {
		width: 766px;
		.game-list-container {
			margin: 0 -7px;
			.game-card-container {
				margin: 0 7px;
				.game-card {
					height: 274px;
					width: 142px;
					.contents {
						height: 192px;
						.hover-info {
							button,
							a.steam,
							a.custom {
								width: 114px;
								&.secondary-button {
									font-size: 9.5px;
									margin-top: 5px;
								}
							}
						}
						.also-available {
							font-size: 10px;
							margin-top: 12px;
						}
						.other-platforms {
							font-size: 11px;
							letter-spacing: -0.22px;
						}
					}
					.info {
						font-size: 11.5px;
						letter-spacing: -0.23px;
						line-height: 1.39;
						.name {
							margin: 10px 0 5px 0;
						}
					}
				}
			}
		}
	}
}

@media (min-width: 1280px) {
	.game-list {
		width: 961px;
		.game-list-container {
			.game-card-container {
				.game-card {
					height: 325px;
					width: 181px;
					.contents {
						height: 243px;
						.hover-info {
							button,
							a.steam,
							a.custom {
								width: 153px;
								&.secondary-button {
									font-size: 10.5px;
									margin-top: 10px;
								}
							}
						}
						.also-available {
							font-size: 11px;
							margin-top: 15px;
						}
						.other-platforms {
							font-size: 12px;
							letter-spacing: -0.24px;
						}
					}
				}
			}
		}
	}
}

@media (min-width: 1440px) {
	.game-list {
		width: 1080px;
		.game-list-container {
			margin: 0 -10px;
			.game-card-container {
				margin: 0 10px;
				.game-card {
					height: 386px;
					width: 200px;
					.contents {
						height: 270px;
						.hover-info {
							button,
							a.steam,
							a.custom {
								height: 48px;
								width: 160px;
								&.primary-button {
									font-size: 11px;
								}
							}
						}
						.also-available {
							font-size: 12px;
							margin-top: 24px;
						}
						.other-platforms {
							font-size: 14px;
							letter-spacing: -0.28px;
							margin-top: 7px;
						}
					}
					.info {
						font-size: 14.5px;
						letter-spacing: -0.29px;
						line-height: 1.55;
						.name {
							margin: 16px 0 6px 0;
						}
					}
				}
			}
		}
	}
}

@media (min-width: 1600px) {
	.game-list {
		width: 1200px;
		.game-list-container {
			.game-card-container {
				.game-card {
					height: 416px;
					width: 224px;
					.contents {
						height: 300px;
						.hover-info {
							button,
							a.steam,
							a.custom {
								height: 52px;
								width: 184px;
								&.primary-button {
									font-size: 12px;
								}
								&.secondary-button {
									font-size: 12px;
								}
							}
						}
						.other-platforms {
							font-size: 14.5px;
							letter-spacing: -0.29px;
						}
					}
				}
			}
		}
	}
}

@media (min-width: 1920px) {
	.game-list {
		width: 1440px;
		.game-list-container {
			.game-card-container {
				.game-card {
					height: 477px;
					width: 272px;
					.contents {
						height: 360px;
						.hover-info {
							button,
							a.steam,
							a.custom {
								width: 232px;
								&.primary-button {
									font-size: 11.5px;
								}
								&.secondary-button {
									font-size: 11.5px;
								}
							}
						}
						.also-available {
							font-size: 11.5px;
						}
					}
				}
			}
		}
	}
}
</style>
